import { Formik } from 'formik';
import React from 'react';
import { Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as Yup from 'yup';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Select from '../../components/FormFields/Select';
import Modal from '../../components/Modal';
import { Header3, P2 } from '../../components/Typography';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_STORE_KEY,
  updateOrganizationInventoryConfirmationDelayRequested,
} from '../../redux/organization/organization.actions';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';
import {
  DELAYED_INVENTORY_CONFIRMATION_HOURS,
  formatInventoryConfirmationTiming,
  getInventoryConfirmationHours,
  INVENTORY_CONFIRMATION_HOURS_OPTIONS,
  InventoryConfirmationHours,
} from './inventory-confirmation-timing';

interface EditInventoryConfirmationDelayModalProps {
  organizationId: string;
  delayedInventoryConfirmationEnabled: boolean;
  onDismiss: () => void;
}

interface EditInventoryConfirmationDelayFormValues {
  hoursAfterStart: InventoryConfirmationHours;
}

const Actions = styled(Row)`
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  padding-top: 24px;
`;

const validationSchema = Yup.object().shape({
  hoursAfterStart: Yup.string().oneOf(INVENTORY_CONFIRMATION_HOURS_OPTIONS).required(),
});

const EditInventoryConfirmationDelayModal: React.FC<EditInventoryConfirmationDelayModalProps> = ({
  delayedInventoryConfirmationEnabled,
  organizationId,
  onDismiss,
}) => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector(createLoadingSelector([UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_STORE_KEY]));
  const [flashMessage, onDismissFlashMessage] = useFlashMessage([
    UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_MODAL_STORE_KEY,
  ]);
  const handleDismiss = () => {
    onDismissFlashMessage();
    onDismiss();
  };

  return (
    <Modal onDismiss={handleDismiss}>
      {flashMessage ? (
        <FlashMessage payload={flashMessage} onDismiss={onDismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}
      <Header3 style={{ marginBottom: 6 }}>Edit Inventory Confirmation Timing</Header3>
      <P2>
        The inventory confirmation is sent 48 hours after the tenancy start date. Opt in to send it 72 hours after the start date.
        Later reminders also move back by a day. This applies to new inventories, and to confirmations rebuilt when a start date
        changes. Confirmations already scheduled keep their current date.
      </P2>
      <Formik<EditInventoryConfirmationDelayFormValues>
        initialValues={{
          hoursAfterStart: getInventoryConfirmationHours(delayedInventoryConfirmationEnabled),
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onDismissFlashMessage();
          dispatch(
            updateOrganizationInventoryConfirmationDelayRequested({
              organizationId,
              delayedInventoryConfirmationEnabled: values.hoursAfterStart === DELAYED_INVENTORY_CONFIRMATION_HOURS,
            }),
          );
        }}
      >
        {({ handleBlur, handleSubmit, setFieldValue, values }) => (
          <form onSubmit={handleSubmit}>
            <FieldWithLabel label="Inventory confirmation">
              <Select
                name="hoursAfterStart"
                aria-label="Inventory confirmation"
                onChange={(value) => setFieldValue('hoursAfterStart', value)}
                onBlur={handleBlur}
                value={values.hoursAfterStart}
              >
                {INVENTORY_CONFIRMATION_HOURS_OPTIONS.map((hours) => (
                  <option key={hours} value={hours}>
                    {formatInventoryConfirmationTiming(hours)}
                  </option>
                ))}
              </Select>
            </FieldWithLabel>
            <Container fluid>
              <Actions>
                <Button buttonType="secondary" noArrow={true} onClick={handleDismiss} type="button">
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="submit">
                  Save
                </Button>
              </Actions>
            </Container>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditInventoryConfirmationDelayModal;
